import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Sparkles } from "lucide-react";
import {
  formatSpecialDate,
  getActiveSpecial,
  getNextContestNumber,
  type SavedGameScope,
  type SpecialDraw,
} from "@/lib/check-scope";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  lotterySlug: string;
  lotteryName?: string;
  /** Texto principal do botão de confirmação. Default: "Salvar e conferir" */
  confirmLabel?: string;
  title?: string;
  description?: string;
  initialScope?: SavedGameScope;
  onConfirm: (payload: {
    scope: SavedGameScope;
    from: number | null;
    until: number | null;
    nextContest: number;
    specialScheduleId: string | null;
    special?: SpecialDraw | null;
  }) => Promise<void> | void;
};

export function CheckScopeDialog({
  open,
  onOpenChange,
  lotterySlug,
  lotteryName,
  confirmLabel = "Salvar e conferir",
  title = "Como conferir estes jogos?",
  description = "Escolha contra quais concursos o conferidor deve avaliar estes jogos. Você pode alterar depois em Meus jogos.",
  initialScope = "future",
  onConfirm,
}: Props) {
  const [scope, setScope] = useState<SavedGameScope>(initialScope);
  const [nextContest, setNextContest] = useState<number | null>(null);
  const [special, setSpecial] = useState<SpecialDraw | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setScope(initialScope);
    let cancelled = false;
    getNextContestNumber(lotterySlug).then((n) => {
      if (!cancelled) setNextContest(n);
    });
    getActiveSpecial(lotterySlug).then((s) => {
      if (!cancelled) setSpecial(s);
    });
    return () => {
      cancelled = true;
    };
  }, [open, lotterySlug, initialScope]);

  const handleConfirm = async () => {
    if (nextContest == null) return;
    const range =
      scope === "special"
        ? { from: special?.contest_number ?? null, until: special?.contest_number ?? null }
        : scope === "future"
          ? { from: nextContest, until: null }
          : { from: nextContest, until: nextContest };
    setBusy(true);
    try {
      await onConfirm({
        scope,
        from: range.from,
        until: range.until,
        nextContest,
        specialScheduleId: scope === "special" ? (special?.id ?? null) : null,
        special: scope === "special" ? special : null,
      });
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{title}</DialogTitle>
          <DialogDescription>
            {description}
            {lotteryName && (
              <span className="mt-1 block text-xs text-muted-foreground">
                Loteria: <strong>{lotteryName}</strong>
                {nextContest != null && <> · Próximo concurso: <strong>#{nextContest}</strong></>}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <RadioGroup value={scope} onValueChange={(v) => setScope(v as SavedGameScope)} className="gap-3 py-2">
          {special && (
            <label
              htmlFor="scope-special"
              className="flex cursor-pointer items-start gap-3 rounded-md border border-primary/50 bg-primary/5 p-3 hover:border-primary"
            >
              <RadioGroupItem value="special" id="scope-special" className="mt-1" />
              <div className="space-y-0.5">
                <Label htmlFor="scope-special" className="flex cursor-pointer items-center gap-1.5 text-sm font-medium">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  {special.special_name ?? "Sorteio especial"}
                </Label>
                <p className="text-xs text-muted-foreground">
                  Confere apenas no sorteio especial de {formatSpecialDate(special.draw_date)}
                  {special.contest_number != null
                    ? ` (concurso #${special.contest_number}).`
                    : ". O número do concurso será vinculado automaticamente quando for divulgado."}
                </p>
              </div>
            </label>
          )}

          <label
            htmlFor="scope-future"
            className="flex cursor-pointer items-start gap-3 rounded-md border border-lt-border p-3 hover:border-primary/40"
          >
            <RadioGroupItem value="future" id="scope-future" className="mt-1" />
            <div className="space-y-0.5">
              <Label htmlFor="scope-future" className="cursor-pointer text-sm font-medium">
                Apenas concursos futuros
              </Label>
              <p className="text-xs text-muted-foreground">
                Confere a partir do próximo concurso{nextContest != null ? ` (#${nextContest})` : ""} em diante.
              </p>
            </div>
          </label>

          <label
            htmlFor="scope-next"
            className="flex cursor-pointer items-start gap-3 rounded-md border border-lt-border p-3 hover:border-primary/40"
          >
            <RadioGroupItem value="next_only" id="scope-next" className="mt-1" />
            <div className="space-y-0.5">
              <Label htmlFor="scope-next" className="cursor-pointer text-sm font-medium">
                Apenas o próximo concurso{nextContest != null ? ` (#${nextContest})` : ""}
              </Label>
              <p className="text-xs text-muted-foreground">
                Confere uma única vez. Depois disso o jogo deixa de ser conferido automaticamente.
              </p>
            </div>
          </label>
        </RadioGroup>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={busy || nextContest == null}>
            {busy ? "Salvando..." : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}